import { ProjectInvoiceTo } from '@consigli/types';
import { FC } from 'react';

import { Field, Fieldset, NumberInput, TextArea, TextInput, Select } from '@/components';

import { CreateProjectFormData } from '../types';

interface BillingInformationFieldsProps {
  formData: CreateProjectFormData;
  errors: Partial<Record<keyof CreateProjectFormData, string>>;
  onFieldChange: (field: keyof CreateProjectFormData, value: unknown) => void;
  invoiceToOptions: Array<{ label: string; value: string }>;
}

export const BillingInformationFields: FC<BillingInformationFieldsProps> = ({
  formData,
  errors,
  onFieldChange,
  invoiceToOptions,
}) => {
  // Check if invoice to project is selected
  const isInvoiceToProject = formData.invoiceTo === ProjectInvoiceTo.PROJECT;

  return (
    <Fieldset legend="Billing Information">
      <Field label="Invoice To" htmlFor="invoice-to-select" error={errors.invoiceTo} required>
        <Select
          options={invoiceToOptions}
          value={
            formData.invoiceTo
              ? invoiceToOptions.find((option) => option.value === formData.invoiceTo)
              : undefined
          }
          onValueChange={(value) => {
            onFieldChange('invoiceTo', value ? value.value : null);
          }}
          placeholder="Select Invoice To"
          className="w-full"
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          showSearch={false}
        />
      </Field>

      {/* Show organization number field only when invoice to project is selected */}
      {isInvoiceToProject && (
        <Field
          label="Organization Number"
          htmlFor="org-number-input"
          error={errors.orgNumber}
          required
        >
          <NumberInput
            placeholder="Enter organization number"
            value={formData.orgNumber}
            onChange={(e) =>
              onFieldChange('orgNumber', e.target.value ? Number(e.target.value) : undefined)
            }
            fullWidth
          />
        </Field>
      )}

      {/* Show billing address field only when invoice to project is selected */}
      {isInvoiceToProject && (
        <Field
          label="Billing Address"
          htmlFor="billing-address-input"
          error={errors.billingAddress}
          required
        >
          <TextInput
            placeholder="Enter billing address"
            fullWidth
            value={formData.billingAddress}
            onChange={(e) => onFieldChange('billingAddress', e.target.value)}
          />
        </Field>
      )}

      {/* Show billing information field only when invoice to project is selected */}
      {isInvoiceToProject && (
        <Field
          label="Billing Information"
          htmlFor="billing-information-textarea"
          error={errors.billingInformation}
        >
          <TextArea
            placeholder="Enter billing information"
            fullWidth
            rows={3}
            value={formData.billingInformation}
            onChange={(e) => onFieldChange('billingInformation', e.target.value)}
          />
        </Field>
      )}
    </Fieldset>
  );
};
